import React, { createContext, useCallback, useState, useContext } from 'react';
import { api } from '../../contracts';
import { Auth, Register } from '../../contracts/auth/dto';
import { local } from '../../storage';

interface SignInCredentials {
  username: string;
  password: string;
}

interface RegisterCredentials {
  name: string;
  email: string;
  phone: string;
  cpf: string;
}

interface AuthContextData {
  user: Auth | null;
  updateUser: (auth: Auth) => void;
  updateTime: number;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  register(credentials: RegisterCredentials): Promise<Register>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

interface ContextFnProps {
  children: React.ReactNode;
}


export const AuthProvider = ({ children }: ContextFnProps) => {
  const [data, setData] = useState<Auth | null>(local.get('user'));
  const [update, setUpdate] = useState<number>(0);

  const updateUser = useCallback((auth: Auth) => {
    const { user, refresh, access, name, coins, chemistry, physics, lastWatchedLesson: last_watched_lesson } = auth;

    const localUser = local.get<Auth>('user');
    if (!localUser) {
      local.set('user', { id: user, refresh, access, name, coins, lastWatchedLesson: last_watched_lesson, visibility: { chemistry, physics } });
    } else {
      localUser.coins = auth.coins;
      localUser.level = auth.level;

      local.set('user', localUser);
    }

    setData((data) => {
      if (!data) {
        return local.get<Auth>('user');
      } else {
        data.coins = auth.coins;
        data.level = auth.level;

        return data;
      }
    });
    setUpdate(new Date().getTime());
  }, []);

  const signIn = useCallback(async ({ username, password }) => {
    const response = await api.auth.authenticate(username, password);
    const { user, isPremium, refresh, access, name, coins, chemistry, physics, lastWatchedLesson: last_watched_lesson } = response;

    local.set('user', { id: user, isPremium, refresh, access, name, coins, lastWatchedLesson: last_watched_lesson, visibility: { chemistry, physics } });

    setData(local.get('user'));
  }, []);


  const signOut = useCallback(() => {
    local.remove('user');

    setData({} as Auth);
  }, []);

  const register = useCallback(async (credential: RegisterCredentials): Promise<Register> => {
    const response = await api.auth.create(credential.name, credential.email, credential.phone, credential.cpf)
    const {fullname, email, phone, cpf} = response // eslint-disable-line @typescript-eslint/no-unused-vars
    return response
  }, [])

  return (
    <AuthContext.Provider
      value={{ user: data, updateTime: update, updateUser, signIn, signOut, register }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}